/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { get, isBoolean } from 'lodash';
import { Row, Col } from 'reactstrap';
import { MDTButtonGroup } from 'mdt-core-components';
import { sendEventTrackingData } from 'mdt-core-components/src/helpers/commonUtils';
import { MDTApi } from 'mdt-core-components/src/services/mdtapi';
import { REGIONS_API_URL } from '../contactUs/constant';
import './surveypopup.css';

const SurveyPopup = ({
    t,
    user,
    isInternalUser,
    externalUsersOnly,
    popupDelayMs = 18000,
    popupFrequencyDays,
    locale,
    defaultLocale,
    culture,
    customerIsoCode,
}) => {
    const [showSurvey, setShowSurvey] = useState(false);
    const [surveyLink, setSurveyLink] = useState('');
    const [region, setRegion] = useState('');

    useEffect(() => {
        getRegions().then((data) => {
            const defaultRegion = getDefaultRegion(data.mdtRegionListData);
            setRegion(defaultRegion);
        });
    }, []);

    useEffect(() => {
        if (!region) return;

        if (popupShouldBeShown(popupFrequencyDays)) {
            showPopupAfterCountDown(popupDelayMs);
        }

        const localKey = `${culture.split('_')[0]}_${customerIsoCode}`;
        const link = getSurveyLink(localKey);

        if (!link) {
            setShowSurvey(false);
            console.error('No valid survey link found for Locale or Locales!');
            return;
        }

        setSurveyLink(link);
    }, [region, popupFrequencyDays, popupDelayMs, culture, customerIsoCode, locale, defaultLocale]);

    function getSurveyLink(localKey) {
        let link = locale && (locale[localKey] || locale[culture.split('_')[0]]);
        if (!link && defaultLocale) {
            link = defaultLocale[region] || defaultLocale[region.split('_')[1]];
        }
        return link;
    }

    const getRegions = () => {
        return MDTApi.call(REGIONS_API_URL, {
            method: 'GET',
            successCallback: (data) => data,
        });
    };

    const getDefaultRegion = (regionsData) => {
        const found = regionsData.find((data) => {
            return data.countryDataList.filter((code) => code.countryIsocode === customerIsoCode)
                .length;
        });
        if (!found) {
            setShowSurvey(false);
            console.error('No region found for the given customerIsoCode!');
            return '';
        }
        const region = found.mdtRegionIsocode.toLowerCase();
        return `${region}_en`;
    };

    const showPopupAfterCountDown = (popupDelayMs) => {
        setTimeout(() => {
            savePopupShown();
            setShowSurvey(true);
        }, popupDelayMs);
    };

    const savePopupShown = () => {
        const lastTimePopupShown = JSON.parse(
            window.localStorage.getItem('lastTimePopupTriggered') || '{}'
        );
        if (user) {
            lastTimePopupShown[user] = new Date().toJSON();
            window.localStorage.setItem(
                'lastTimePopupTriggered',
                JSON.stringify(lastTimePopupShown)
            );
        }
    };

    const popupShouldBeShown = (popupFrequencyDays = 30) => {
        const popupReappeareance = 1000 * 60 * 60 * 24 * popupFrequencyDays;
        let lastTimePopupShown;

        if (externalUsersOnly && isInternalUser) {
            return false;
        }

        // NOTE: If we don't have info about the user, we can't show a survey popup to it 20211007:Alevale
        if (user) {
            lastTimePopupShown = JSON.parse(
                window.localStorage.getItem('lastTimePopupTriggered') || '{}'
            );

            // NOTE: If never shown or it's been a month since the last time the popup
            // was shown to the user in this machine show it again 20211007:Alevale
            if (
                !lastTimePopupShown[user] ||
                new Date(lastTimePopupShown[user]).getTime() + popupReappeareance <
                    new Date().getTime()
            ) {
                return true;
            }
            return false;
        }
    };

    const close = () => {
        const eventParamObj = {
            eventCategory: 'cta',
            eventAction: 'Survey Popup',
            processActions: false,
            eventLabel: 'Survey Cancel',
            processLabels: false,
        };
        sendEventTrackingData(eventParamObj);
        setShowSurvey(false);
    };

    const openSurvey = () => {
        const eventParamObj = {
            eventCategory: 'cta',
            eventAction: 'Survey Popup',
            processActions: false,
            eventLabel: 'Survey Accept',
            processLabels: false,
        };
        sendEventTrackingData(eventParamObj);
        window.open(surveyLink, '_blank');
        close();
    };

    const buttons = [
        {
            type: 'button',
            onClick: close,
            disabled: false,
            label: t('surveyCancel'),
            className: 'alternatePrimary',
        },
        {
            type: 'button',
            onClick: openSurvey,
            disabled: false,
            name: 'mdt-button',
            label: t('surveyAccept'),
            className: 'primary',
        },
    ];

    return (
        showSurvey && (
            <div
                role='dialog'
                aria-live='polite'
                aria-label='cookieconsent'
                aria-describedby='cookieconsent:desc'
                className='survey-box cc-banner cc-type-info cc-theme-block cc-bottom'
            >
                <Row>
                    <Col xs={10}>
                        <h2>{t('surveyTitle')}</h2>
                    </Col>
                    <Col xs={2}>
                        <h2 className='s-high close-button' onClick={close}>
                            x
                        </h2>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <p className='survey-text body1'>{t('surveyText')}</p>
                    </Col>
                </Row>
                <MDTButtonGroup buttons={buttons} />
            </div>
        )
    );
};

SurveyPopup.propTypes = {
    user: PropTypes.string.isRequired,
    isInternalUser: PropTypes.bool.isRequired,
    externalUsersOnly: PropTypes.bool.isRequired,
    popupDelayMs: PropTypes.number.isRequired,
    popupFrequencyDays: PropTypes.number.isRequired,
};

SurveyPopup.defaultProps = {};

export default connect(
    (state) => ({
        user: get(state, 'meDetails.user.pk'),
        culture: get(state, 'meDetails.user.culture'),
        customerIsoCode: get(state, 'meDetails.defaultCustomer.soldTo.address.country.isocode'),
        isInternalUser: isBoolean(state.meDetails.isInternal)
            ? state.meDetails.isInternal
            : get(state, 'meDetails.isInternal', 'false').toLowerCase() === 'true',
        externalUsersOnly: get(
            state,
            'formConfig.featureConfiguration.VoiceOfCustomer.configuration.externalUsersOnly',
            true
        ),
        popupDelayMs: get(
            state,
            'formConfig.featureConfiguration.VoiceOfCustomer.configuration.popupDelayMs',
            18000
        ),
        popupFrequencyDays: get(
            state,
            'formConfig.featureConfiguration.VoiceOfCustomer.configuration.popupFrequencyDays',
            31
        ),
        locale: get(
            state,
            'formConfig.featureConfiguration.VoiceOfCustomer.configuration.locale',
            {}
        ),
        defaultLocale: get(
            state,
            'formConfig.featureConfiguration.VoiceOfCustomer.configuration.defaultLocale',
            {}
        ),
    }),
    {}
)(withTranslation()(SurveyPopup));
